/**
 * The "bank" easter-egg sound cue (ADR-0078). When any **player** line contains the
 * word "bank" - case-insensitive, anywhere in the string - every client that hears
 * that speech plays the UO Kryss hit sound (`sword7`), a nod to the room's name.
 *
 * Like {@link awardCue}, this keeps the *decision* (does this line sound the cue?)
 * pure, snapshot-free, and unit-tested, separate from {@link AudioManager}'s
 * *playback*. The cue is purely client-side: it is derived from the {@link
 * SpeechMessage} every client already receives, so there is no protocol/sim change
 * (mirrors the positional sound model, ADR-0092). NPC barks are excluded via the
 * server-stamped {@link SpeechMessage.npc} flag (ADR-0075) - only real player speech
 * triggers it. System **herald** lines (the `/top` standings, the Rat Swarm
 * announcements) are excluded too: they are not player chatter, and the swarm-start
 * herald literally names the bank, which would otherwise misfire this cue over the
 * top of the event-start scream ({@link swarmCue}). **Lord Blackthorn**'s storm taunts
 * are excluded on the same footing (ADR-0156): they journal like a herald (`npc = false`)
 * but are a boss's voice, not player chatter, and one taunt names the bank - so the cue
 * must not fire on them. Playback still routes through {@link AudioManager.play}, so the
 * master mute (settings panel) governs it.
 */
import type { SoundEvent } from './soundObserver';
import { LEADERBOARD_HERALD_NAME, type SpeechMessage } from '../net/protocol';
import { BLACKTHORN_NAME } from '../sim';

/**
 * The bank cue plays at full volume: it is a UI easter egg heard by everyone who
 * received the line, not a sound positioned in the world, so it is not
 * proximity-scaled the way footsteps/doors are (matches {@link awardCue}).
 */
const BANK_VOLUME = 1;

/**
 * The substring that triggers the cue, matched case-insensitively anywhere in the
 * line (so "bank", "BANK", "let's go to the bank", and even "embankment" all fire -
 * the captain asked for "bank as part of a string", i.e. a plain substring, not a
 * word boundary).
 */
const BANK_PATTERN = /bank/i;

/**
 * The {@link SoundEvent} to play for a speech line, or `null` when it should be
 * silent. Sounds the Kryss-hit cue for any **player** line (NPC barks excluded via
 * {@link SpeechMessage.npc}) whose text contains "bank" case-insensitively;
 * everything else is silent here.
 */
export function bankCue(speech: SpeechMessage): SoundEvent | null {
  if (
    speech.npc === true ||
    speech.name === LEADERBOARD_HERALD_NAME ||
    speech.name === BLACKTHORN_NAME
  ) {
    return null;
  }
  return BANK_PATTERN.test(speech.text) ? { sound: 'sword7', volume: BANK_VOLUME } : null;
}
